import React, { useState, useEffect, useRef } from 'react';
import './Library.css';
import Modal from '../common/Modal';
import { auth } from '../../services/api';
import { formatCurrency, formatDate, toLocalDateString } from '../../utils/format';

interface Invoice {
  id: string;
  clientName: string;
  propertyName: string;
  propertyAddress: string;
  invoiceDate: string;
  dueDate: string;
  utilityType: string;
  accountNumber: string;
  amount: number;
  status: string;
  fileName: string;
  isArchived: boolean;
  // True when the invoice matches a supplier_contracts row (server-determined).
  // UI uses this to render "Supplier Contract" in the Property column instead of
  // "Unknown Property / No address" — supplier invoices have no master-level property.
  isSupplier?: boolean;
}

const Library: React.FC = () => {
  // Calculate default date range (1 month)
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  // Search fields
  const [client, setClient] = useState('');
  const [startDate, setStartDate] = useState(toLocalDateString(oneMonthAgo));
  const [endDate, setEndDate] = useState(toLocalDateString(today));
  const [utility, setUtility] = useState('');

  // Advanced fields
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [property, setProperty] = useState('');
  const [account, setAccount] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Results
  const [results, setResults] = useState<Invoice[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // PDF Modal
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [currentFileName, setCurrentFileName] = useState('');

  // Typeahead state
  const [clientSuggestions, setClientSuggestions] = useState<string[]>([]);
  const [propertySuggestions, setPropertySuggestions] = useState<string[]>([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const propertyInputRef = useRef<HTMLInputElement>(null);

  const getUtilityIcon = (type: string) => {
    const icons = {
      'Electric': '⚡',
      'Gas': '🔥',
      'Water': '💧',
      'CWS': '❄️'
    };
    return icons[type as keyof typeof icons] || '📄';
  };
  
  // Fetch client suggestions
  useEffect(() => {
    if (client.length < 1) {
      setClientSuggestions([]);
      setShowClientDropdown(false);
      return;
    }
    
    const fetchSuggestions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/library/typeahead/clients?q=${encodeURIComponent(client)}`,
          { headers: { ...auth.getAuthHeaders() } }
        );
        if (response.ok) {
          const data = await response.json();
          setClientSuggestions(data);
        }
      } catch (error) {
        console.error('Error fetching client suggestions:', error);
      }
    };
    
    const timer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timer);
  }, [client]);
  
  // Fetch property suggestions
  useEffect(() => {
    if (property.length < 1) {
      setPropertySuggestions([]);
      setShowPropertyDropdown(false);
      return;
    }
    
    const fetchSuggestions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/library/typeahead/properties?q=${encodeURIComponent(property)}`,
          { headers: { ...auth.getAuthHeaders() } }
        );
        if (response.ok) {
          const data = await response.json();
          setPropertySuggestions(data);
        }
      } catch (error) {
        console.error('Error fetching property suggestions:', error);
      }
    };
    
    const timer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timer);
  }, [property]);
  
  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientInputRef.current && !clientInputRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
      if (propertyInputRef.current && !propertyInputRef.current.contains(event.target as Node)) {
        setShowPropertyDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    
    try {
      const params = new URLSearchParams();
      if (client) params.append('clientName', client);
      if (startDate) params.append('dateFrom', startDate);
      if (endDate) params.append('dateTo', endDate);
      if (utility) params.append('utilityType', utility);
      if (property) params.append('propertyAddress', property);
      if (account) params.append('accountNumber', account);
      if (minAmount) params.append('amountMin', minAmount);
      if (maxAmount) params.append('amountMax', maxAmount);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/library/search?${params}`, {
        headers: { ...auth.getAuthHeaders() }
      });
      const data = await response.json();
      
      setResults(data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };
  
  const clearAll = () => {
    setClient('');
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    setStartDate(toLocalDateString(oneMonthAgo));
    setEndDate(toLocalDateString(today));
    setUtility('');
    setProperty('');
    setAccount('');
    setMinAmount('');
    setMaxAmount('');
    setResults([]);
    setSearched(false);
    setShowAdvanced(false);
  };
  
  const exportCSV = async () => {
    const params = new URLSearchParams();
    if (client) params.append('clientName', client);
    if (startDate) params.append('dateFrom', startDate);
    if (endDate) params.append('dateTo', endDate);
    if (utility) params.append('utilityType', utility);
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/library/export?${params}`, {
      headers: { ...auth.getAuthHeaders() }
    });
    const blob = await response.blob();
    
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices_${toLocalDateString(new Date())}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const viewFile = async (invoiceId: string, fileName: string) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/library/download/${invoiceId}`, {
      headers: { ...auth.getAuthHeaders() }
    });
    const data = await response.json();
    setPdfUrl(data.url);
    setCurrentFileName(fileName);
    setShowPdfModal(true);
  };

  const downloadFile = async () => {
    if (pdfUrl) {
      try {
        const response = await fetch(pdfUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = currentFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };
  
  const filteredResults = results.filter(r => 
    activeTab === 'active' ? !r.isArchived : r.isArchived
  );
  
  const activeCount = results.filter(r => !r.isArchived).length;
  const archivedCount = results.filter(r => r.isArchived).length;

  return (
    <div className="library-container">
      {/* Search Box */}
      <div className="search-box">
        <h2 className="search-header">Invoice Search</h2>
        <div className="search-grid">
          {/* Row 1 */}
          <input
            ref={clientInputRef}
            type="text"
            placeholder="Client name"
            value={client}
            onChange={(e) => {
              setClient(e.target.value);
              setShowClientDropdown(true);
            }}
            onFocus={() => clientSuggestions.length > 0 && setShowClientDropdown(true)}
            className="input-field col-span-2"
            autoComplete="off"
          />
          <select
            value={utility}
            onChange={(e) => setUtility(e.target.value)}
            className="input-field"
          >
            <option value="">All Utilities</option>
            <option value="Electric">Electric</option>
            <option value="Gas">Gas</option>
            <option value="Water">Water</option>
            <option value="CWS">CWS</option>
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input-field"
            placeholder="From Date"
            title="From Date"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="input-field"
            placeholder="To Date"
            title="To Date"
          />
          <div className="action-buttons">
            <button onClick={handleSearch} className="btn-icon search" disabled={loading} title="Search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </button>
            <button
              className="btn-icon advanced"
              onClick={() => setShowAdvanced(!showAdvanced)}
              title={showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={showAdvanced ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"}/>
              </svg>
            </button>
            <button onClick={clearAll} className="btn-icon clear" title="Clear All">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18"/>
                <path d="M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          {/* Row 2 - Advanced */}
          {showAdvanced && (
            <>
              <input
                ref={propertyInputRef}
                type="text"
                placeholder="Property name"
                value={property}
                onChange={(e) => {
                  setProperty(e.target.value);
                  setShowPropertyDropdown(true);
                }}
                onFocus={() => propertySuggestions.length > 0 && setShowPropertyDropdown(true)}
                className="input-field"
                autoComplete="off"
              />
              <input
                type="text"
                placeholder="Account #"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                className="input-field"
              />
              <input
                type="number"
                placeholder="Min amount"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className="input-field"
                step="0.01"
              />
              <input
                type="number"
                placeholder="Max amount"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                className="input-field"
                step="0.01"
              />
              <div></div> {/* Empty column for alignment */}
            </>
          )}
        </div>
        
        
        {/* Typeahead dropdowns */}
        {showClientDropdown && clientSuggestions.length > 0 && clientInputRef.current && (
          <div 
            className="typeahead-dropdown"
            style={{
              position: 'fixed',
              top: clientInputRef.current.getBoundingClientRect().bottom + window.scrollY + 'px',
              left: clientInputRef.current.getBoundingClientRect().left + 'px',
              width: clientInputRef.current.getBoundingClientRect().width + 'px',
              zIndex: 1000
            }}
          >
            {clientSuggestions.map((suggestion, index) => (
              <div
                key={index}
                className="typeahead-item"
                onClick={() => {
                  setClient(suggestion);
                  setShowClientDropdown(false);
                }}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
        
        {showPropertyDropdown && propertySuggestions.length > 0 && propertyInputRef.current && (
          <div 
            className="typeahead-dropdown"
            style={{
              position: 'fixed',
              top: propertyInputRef.current.getBoundingClientRect().bottom + window.scrollY + 'px',
              left: propertyInputRef.current.getBoundingClientRect().left + 'px',
              width: propertyInputRef.current.getBoundingClientRect().width + 'px',
              zIndex: 1000
            }}
          >
            {propertySuggestions.map((suggestion, index) => (
              <div
                key={index}
                className="typeahead-item"
                onClick={() => {
                  setProperty(suggestion);
                  setShowPropertyDropdown(false);
                }}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}

      </div>

      {/* PDF Modal */}
      {showPdfModal && (
        <Modal
          isOpen={showPdfModal}
          onClose={() => setShowPdfModal(false)}
          title={currentFileName}
          size="large"
        >
          <iframe
            src={pdfUrl}
            style={{ width: '100%', height: '75vh', border: 'none' }}
            title="Invoice PDF"
          />
        </Modal>
      )}

      {/* Results */}
      {searched && (
        <div className="results-box">
          <div className="results-header">
            <div className="tabs">
              <button
                className={`tab ${activeTab === 'active' ? 'active' : ''}`}
                onClick={() => setActiveTab('active')}
              >
                Active ({activeCount})
              </button>
              <button
                className={`tab ${activeTab === 'archived' ? 'active' : ''}`}
                onClick={() => setActiveTab('archived')}
              >
                Archived ({archivedCount})
              </button>
            </div>
            {filteredResults.length > 0 && (
              <button onClick={exportCSV} className="btn-export">Export CSV</button>
            )}
          </div>
          
          {filteredResults.length > 0 ? (
            <table className="results-table">
              <colgroup>
                <col style={{ width: '17%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Property</th>
                  <th>Invoice</th>
                  <th className="text-center">Utility</th>
                  <th>Account</th>
                  <th className="text-center">Amount</th>
                  <th className="text-center">Invoice Date</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <div className="client-info">
                        <div className="client-name">{invoice.clientName}</div>
                        <div className="client-address">{invoice.propertyAddress}</div>
                      </div>
                    </td>
                    <td>
                      <div className="property-info">
                        {invoice.isSupplier ? (
                          <div className="property-name supplier-row-label">Supplier Contract</div>
                        ) : (
                          <>
                            <div className="property-name">{invoice.propertyName}</div>
                            <div className="property-address">{invoice.propertyAddress}</div>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="invoice-info">
                        <div className="file-name">{invoice.fileName}</div>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className="utility-icon">{getUtilityIcon(invoice.utilityType)}</span>
                    </td>
                    <td className="account">{invoice.accountNumber}</td>
                    <td className="amount text-center">{formatCurrency(invoice.amount)}</td>
                    <td className="text-center">
                      <div className="invoice-date">
                        {formatDate(invoice.invoiceDate)}
                      </div>
                    </td>
                    <td className="text-center">
                      <button onClick={() => viewFile(invoice.id, invoice.fileName)} className="action-icon-btn view" title="View invoice">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-results">
              No {activeTab} invoices found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Library;